import { useEffect, useRef, useState } from 'react';
import DuplicateFileModal from 'src/components/DuplicateFileModal';
import { extractUploadedFiles } from 'src/utils/extractUploadedFiles';

interface TFileItem {
  fieldName: string;
  name: string;
}
const LABEL_MAP: Record<string, string> = {
  ARRIVEFiles: 'ARRIVE Checklist',
  AdditionalSupplementary: 'Supplementary File(s) (NOT for review)',
  CONSORTFiles: 'CONSORT Checklist',
  CoverLetter: 'Cover Letter',
  Figures: 'Figures',
  Tables: 'Tables',
  Manuscript: 'Manuscript',
  TitlePage: 'Title Page',
  Supplementary: 'Supplementary File(s) (for review)',
  StandardReportingGuidelinesFileDetails: 'Standard Reporting Guidelines',
  GraphicalAbstractImage: 'Graphical Abstract'
};
const getFieldLabel = (fieldName: string | null | undefined): string => {
  if (!fieldName) {
    return '';
  }
  return (
    LABEL_MAP[fieldName] ??
    fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, c => c.toUpperCase())
      .trim()
  );
};
export function useDuplicateConfirm() {
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [duplicateFile, setDuplicateFile] = useState<TFileItem | null>(null);

  const checkIsDuplicateFile = async (
    file: TFileItem,
    files: unknown
  ): Promise<boolean> => {
    if (!file?.name) {
      return false;
    }
    // Prevent multiple pending confirmations
    if (resolverRef.current) {
      return false;
    }

    try {
      const fileItems = extractUploadedFiles(files) ?? [];
      const duplicate = Array.isArray(fileItems)
        ? fileItems?.filter(item => item?.name === file?.name)
        : undefined;

      if (!duplicate || !duplicate?.length) {
        return true;
      }
      const fieldNames = [
        ...new Set(
          duplicate
            ?.map(file => file?.fieldName)
            .filter((fieldName): fieldName is string => Boolean(fieldName))
        )
      ]
        .map(getFieldLabel)
        .join(' and ');

      const duplicateAllfileName = {
        name: duplicate[0]?.name ?? '',
        fieldName: fieldNames ?? ''
      };
      setDuplicateFile(duplicateAllfileName);
      setIsOpen(true);

      return new Promise<boolean>(resolve => {
        resolverRef.current = resolve;
      });
    } catch (error) {
      // Fail open (or return false if you want to block uploads)
      return true;
    }
  };

  const close = (result: boolean) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }

    setIsOpen(false);
    setDuplicateFile(null);
  };

  // Resolve pending promise if hook unmounts
  useEffect(() => {
    return () => {
      if (resolverRef.current) {
        resolverRef.current(false);
        resolverRef.current = null;
      }
    };
  }, []);

  const duplicateFileModal = (
    <DuplicateFileModal
      isOpen={isOpen}
      fieldName={duplicateFile?.fieldName}
      fileName={duplicateFile?.name}
      onContinue={() => close(true)}
      onCancel={() => close(false)}
    />
  );

  return {
    checkIsDuplicateFile,
    duplicateFileModal
  };
}
